"use client"

import React, { useState, useCallback } from 'react';
import { useScroll, useTransform, motion, AnimatePresence } from 'motion/react';
import EntryPoints from '../../framework/nav/EntryPoints';
import { NavItem } from '../../framework/nav/NavItem';

const images = [
  "https://i.postimg.cc/0N8D2TwW/1.jpg",
  "https://i.postimg.cc/FK93sMLD/2.jpg",
  "https://i.postimg.cc/W42gbBky/3.jpg",
  "https://i.postimg.cc/TPRgYzD4/4.jpg",
  "https://i.postimg.cc/wB6LTCmZ/5.jpg",
  "https://i.postimg.cc/KcnHJv3j/6.jpg",
  "https://i.postimg.cc/wB0JtwXF/7.jpg",
  "https://i.postimg.cc/xdtHk6vt/8.jpg",
  "https://i.postimg.cc/FFjBDsk7/9.jpg",
  "https://i.postimg.cc/JhPJypNd/10.jpg",
  "https://i.postimg.cc/g0NRwgVS/11.jpg",
  "https://i.postimg.cc/rpQWdf1P/12.jpg",
  "https://i.postimg.cc/CKmDZcGt/13.jpg",
  "https://i.postimg.cc/QM05BfgD/14.jpg",
  "https://i.postimg.cc/k5f8VTQk/15.jpg",
  "https://i.postimg.cc/xdtHk6GD/16.jpg",
  "https://i.postimg.cc/g0NRwgyW/17.jpg",
  "https://i.postimg.cc/BvMD8m54/18.jpg",
  "https://i.postimg.cc/GpqYBXPc/19.jpg",
];

const bridgeDay = "https://i.postimg.cc/YSbg4dNS/20.png";
const bridgeNight = "https://i.postimg.cc/HLB5J60s/20-5.png";

const Lightbox = ({ src, onClose }: { src: string; onClose: () => void }) => {
  return (
    <AnimatePresence>
      <motion.div
        key="lightbox-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          backgroundColor: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'zoom-out',
          padding: '24px',
        }}
      >
        <motion.img
          key="lightbox-img"
          src={src}
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.7, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          onClick={(e) => e.stopPropagation()}
          alt="Full size"
          style={{
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: 'auto',
            height: 'auto',
            borderRadius: '12px',
            objectFit: 'contain',
            boxShadow: '0 25px 80px rgba(0,0,0,0.6)',
            cursor: 'default',
          }}
        />
        <button
          onClick={onClose}
          style={{
            position: 'fixed',
            top: '20px',
            right: '24px',
            background: 'rgba(255,255,255,0.15)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'white',
            fontSize: '20px',
            lineHeight: 1,
            backdropFilter: 'blur(6px)',
          }}
          aria-label="Close"
        >
          ×
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

const ParallaxScroll = ({ images }: { images: string[] }) => {
  const { scrollYProgress } = useScroll();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleOpen = useCallback((src: string) => setSelectedImage(src), []);
  const handleClose = useCallback(() => setSelectedImage(null), []);

  // Column 1 scrolls faster (moves up more), column 2 is neutral, column 3 scrolls slower
  const translateYFirst = useTransform(scrollYProgress, [0, 1], [0, -300]);
  const translateYThird = useTransform(scrollYProgress, [0, 1], [0, -150]);

  const third = Math.ceil(images.length / 3);
  const firstPart = images.slice(0, third);
  const secondPart = images.slice(third, 2 * third);
  const thirdPart = images.slice(2 * third);

  return (
    <div className="w-full">
      {selectedImage && <Lightbox src={selectedImage} onClose={handleClose} />}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 items-start max-w-5xl mx-auto gap-10 py-20 px-10">
        <div className="grid gap-10">
          {firstPart.map((el, idx) => (
            <motion.div style={{ y: translateYFirst }} key={"grid-1" + idx}>
              <img
                src={el}
                className="h-80 w-full object-cover object-center rounded-lg !m-0 !p-0"
                height="400"
                width="400"
                alt="thumbnail"
                onClick={() => handleOpen(el)}
                style={{ cursor: 'zoom-in' }}
              />
            </motion.div>
          ))}
        </div>
        <div className="grid gap-10">
          {secondPart.map((el, idx) => (
            <motion.div key={"grid-2" + idx}>
              <img
                src={el}
                className="h-80 w-full object-cover object-center rounded-lg !m-0 !p-0"
                height="400"
                width="400"
                alt="thumbnail"
                onClick={() => handleOpen(el)}
                style={{ cursor: 'zoom-in' }}
              />
            </motion.div>
          ))}
        </div>
        <div className="grid gap-10">
          {thirdPart.map((el, idx) => (
            <motion.div style={{ y: translateYThird }} key={"grid-3" + idx}>
              <img
                src={el}
                className="h-80 w-full object-cover object-center rounded-lg !m-0 !p-0"
                height="400"
                width="400"
                alt="thumbnail"
                onClick={() => handleOpen(el)}
                style={{ cursor: 'zoom-in' }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PhotographyView = () => {
  return (
    <div className='main-container-photography'>
      <div className="photography-header">
        <h1>Photography</h1>
      </div>
      <ParallaxScroll images={images} />
    </div>
  );
};

class PhotographyEntryPoints implements EntryPoints {
  renderFullView = (navItem: NavItem, secondaryNavItems: string[], width: number, height: number): any => {
    return <PhotographyView />;
  }

  renderIntroView = (navItem: NavItem, secondaryNavItems: string[]): any => {
    return (
      <span>Photography intro</span>
    );
  }
}

const photographyEntryPoints = new PhotographyEntryPoints();
export default photographyEntryPoints;
